/**
 * ButterUI — Theme Cache
 *
 * The persistence half of the {@link TenantProvider} contract. A `ThemeCache`
 * remembers the last-known palette for a tenant so the next cold start can paint
 * the correct brand colour *before* any network call resolves.
 *
 * ## The key is mandatory, and that is the point
 *
 * `tenants.colorTheme` is only knowable at runtime, so first paint can't know it
 * — hence a cache. But several surfaces serve MANY tenants from ONE origin: the
 * booking portal resolves its tenant from `?tenant-handle=`, and the admin portal
 * shares one origin outside production. A single unscoped key means tenant B's
 * first paint inherits tenant A's brand — a cache added to prevent a flash of the
 * neutral default instead causing a flash of someone else's brand.
 *
 * {@link createThemeCache} therefore takes the tenant handle and derives the key
 * itself. There is no default key and no unscoped export, so cross-tenant bleed
 * is impossible by construction rather than by convention.
 *
 * ## Sync or async
 *
 * `get()` may return a value or a Promise. `localStorage` answers synchronously,
 * so the web apps apply the cached theme in the same tick they mount — no flash.
 * Capacitor's `Preferences` only answers via a Promise. The provider branches on
 * the return value, so neither adapter has to fake the other's shape.
 *
 * @see TenantProvider.tsx for how each phase consumes this
 */

/** Namespace for every derived cache key. */
export const THEME_CACHE_KEY_PREFIX = 'colorTheme';

/**
 * Persistence adapter for a tenant's cached palette.
 *
 * Implementations MUST NOT throw — a dead cache is a degraded experience (one
 * frame of the default brand), never a broken app. Swallow and return `null`.
 */
export interface ThemeCache {
  /** Last persisted palette name, or `null` when nothing is cached. */
  get(): string | null | Promise<string | null>;
  /** Persist a palette name for the next cold start. */
  set(themeName: string): void | Promise<void>;
}

/**
 * Derive the storage key for a tenant. Exported for tests and for adapters that
 * need the same namespacing over different storage (e.g. Capacitor).
 */
export function themeCacheKey(tenantHandle: string): string {
  return `${THEME_CACHE_KEY_PREFIX}:${tenantHandle}`;
}

/**
 * Build a synchronous `localStorage` cache scoped to one tenant.
 *
 * Reads and writes are guarded: Safari private mode and cookie-blocking policies
 * throw on access, and a theme cache is never worth taking the app down for.
 *
 * @param tenantHandle - The tenant this cache belongs to. Required — see the
 *   module docs for why there is no unscoped default.
 *
 * @example
 * const cache = useMemo(() => (handle ? createThemeCache(handle) : undefined), [handle]);
 * <TenantProvider handle={handle} colorTheme={theme} cache={cache}>
 */
export function createThemeCache(tenantHandle: string): ThemeCache {
  const key = themeCacheKey(tenantHandle);

  return {
    get() {
      if (typeof window === 'undefined') return null;
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(themeName: string) {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(key, themeName);
      } catch {
        // Storage unavailable (private mode, quota, blocked cookies) — the theme
        // still applies for this session, it just won't survive a reload.
      }
    },
  };
}
